import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { useAtomValue } from "jotai";
import { initReactI18next } from "react-i18next";
import { userLocalPreferences } from "./state/preferences";

/**
 * Defines the list of languages supported by this application.
 */
export enum SupportedLanguages {
	English = "en",
	Italian = "it",
}

/**
 * Defines the list of languages supported by this application as an iterable array.
 */
export const SupportedLanguagesList = [SupportedLanguages.English, SupportedLanguages.Italian];

/**
 * Support hook for getting language used by the current user.
 * This is useful in various scenarios, for example when building query cache keys.
 *
 * If no user preference or browser language has been set, the result of this hook
 * falls back to SupportedLanguages.English.
 *
 * @returns the suggested language used by the current user
 */
export const useCurrentLanguage = (): string => {
	const preferences = useAtomValue(userLocalPreferences);

	return preferences.language ?? preferences.browserLanguage ?? SupportedLanguages.English;
};

i18n.use(Backend)
	.use(LanguageDetector)
	.use(initReactI18next)
	.init({
		ns: [
			"common",
			"errors",
			"navigation",
			"authentication",
			"format.numbers",
			"validation",
			"snackbar",
			"feature.welcome",
			"feature.account",
			"feature.network",
			"feature.results",
			"feature.users",
			"feature.admin",
			"feature.carbonsnap",
		],
		defaultNS: "common",
		fallbackLng: {
			"it-it": [SupportedLanguages.Italian],
			"en-us": [SupportedLanguages.English],
			default: [SupportedLanguages.English],
		},
		load: "languageOnly",
		keySeparator: "/",
		supportedLngs: ["en", "it", "it-it", "it-IT"],
		interpolation: {
			escapeValue: false, // Not needed for react as it escapes by default.
		},
		react: {
			transSupportBasicHtmlNodes: true,
			transKeepBasicHtmlNodesFor: ["br", "strong", "i", "b"],
			useSuspense: false,
		},
	});

// Extend dayjs so that it can format dates with localization support.
dayjs.extend(localizedFormat);

export default i18n;
